import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "@/context/AuthContext";
import {
  listTrips,
  upsertTrip,
  deleteTrip as deleteServerTrip,
  deleteAllTrips as deleteAllServerTrips,
} from "@/lib/trip-store";

// ===== Types =====
export type TimeBucket = "morning" | "afternoon" | "evening" | "night";

export type ItemType =
  | "activity"
  | "dining"
  | "accommodation"
  | "transport"
  | "nightlife"
  | "tip";

// A decision fork the AI presented as tappable cards (present_options
// tool). Lives on the assistant Message that presented it; chosenLabel is
// set when the user taps, so answered cards render inert after reload.
export interface DecisionOption {
  label: string;
  description: string;
  pros?: string[];
  cons?: string[];
}

export interface MessageOptions {
  question: string;
  items: DecisionOption[];
  chosenLabel?: string;
}

export interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: number;
  // Never sent to the API: messagesForAPI maps to { role, content } only.
  options?: MessageOptions;
}

export interface ItineraryItem {
  id: string;
  timeBucket: TimeBucket;
  specificTime?: string;
  title: string;
  description: string;
  type: ItemType;
  coordinates: { lat: number; lng: number };
  address?: string;
  hours?: string;
  priceRange?: string;
  bookingUrl?: string;
  notes?: string;
  duration?: number;
  googlePlaceId?: string;
  // A stand-in place used ONLY for the card's photo: the area the stop is
  // in, or the destination, for stops that name no place of their own.
  // Never used for the Maps link, which must open the stop, not its
  // neighbourhood.
  photoPlaceId?: string;
  // Why this place was picked: an editorial mention, community consensus,
  // award, etc. Populated by the AI after verify_place + a web_search for
  // a credible source. Surfaced as a small badge on item cards so users
  // see provenance rather than treating every pick as "the AI said so".
  source?: { label: string; url?: string };
  // Booking-specific fields, mostly used by accommodation items but
  // available to any item that represents a confirmed reservation
  // (a ticketed tour, a restaurant booking, etc.). Presence of
  // bookingReference signals "confirmed" vs just a suggestion.
  checkIn?: string;
  checkOut?: string;
  bookingReference?: string;
}

export interface TripEssential {
  topic: string;
  content: string;
  url?: string;
}

export interface Day {
  id: string;
  date: string;
  title: string;
  // The traveler's own reminders for the day ("From the day · notes"),
  // filed by hand from the panel or by the concierge via add_day_note.
  notes?: string[];
  items: ItineraryItem[];
}

export interface Trip {
  id: string;
  title: string;
  destination: string;
  startDate: string | null;
  endDate: string | null;
  coverImage: string;
  days: Day[];
  chatHistory: Message[];
  // Practical things to know before going (visa, plugs, SIM, tipping,
  // transit, scams). Populated by the AI via set_essentials during the
  // initial build. Rendered as a "Things to know" card on the trip page.
  essentials?: TripEssential[];
  createdAt: string;
  updatedAt: string;
}

interface JourneyContextType {
  journeys: Trip[];
  // True while the signed-in user's trips are still loading from the server.
  // Consumers MUST wait for this to be false before concluding a trip id
  // "doesn't exist" (otherwise they'd create an empty trip that overwrites
  // the real one once it loads).
  journeysLoading: boolean;
  getJourney: (id: string) => Trip | undefined;
  saveJourney: (trip: Trip) => void;
  updateJourney: (id: string, updater: (prev: Trip) => Trip) => void;
  deleteJourney: (id: string) => void;
  clearAllJourneys: () => void;
  createEmptyJourney: (id?: string) => Trip;
}

const STORAGE_KEY = "sundayatlas_journeys";

// When signed in, trips persist to Supabase; when anonymous, to
// localStorage. The provider keeps a single in-memory `journeys` array as
// the source of truth the UI reads synchronously, and write-through to the
// active backend happens in the background. This keeps the 6-method
// interface (and every consumer) unchanged.

const JourneyContext = createContext<JourneyContextType | undefined>(undefined);

const generateUuid = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `journey-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const buildEmptyTrip = (id: string): Trip => {
  const now = new Date().toISOString();
  return {
    id,
    title: "New trip",
    destination: "",
    startDate: null,
    endDate: null,
    coverImage: "",
    days: [],
    chatHistory: [
      {
        id: `msg-${Date.now()}`,
        role: "assistant",
        content:
          "Hey, I'm your travel concierge. Tell me about the trip you're planning: where, when, what kind of vibe. I'll start building it out as we chat.",
        timestamp: Date.now(),
      },
    ],
    createdAt: now,
    updatedAt: now,
  };
};

function readLocalJourneys(): Trip[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? (JSON.parse(stored) as Trip[]) : [];
  } catch {
    return [];
  }
}

export function JourneyProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [journeys, setJourneys] = useState<Trip[]>(() => readLocalJourneys());
  // Loading is true only while a signed-in user's server trips are still in
  // flight. Anonymous state is synchronous (localStorage), so it starts false
  // unless auth itself is still resolving AND a session might exist. We start
  // it as authLoading so consumers don't prematurely decide a trip is missing
  // on a signed-in refresh (the bug that overwrote real trips with empty ones).
  const [journeysLoading, setJourneysLoading] = useState<boolean>(authLoading);

  // Internal ref for synchronous reads inside callbacks that fire OUTSIDE the
  // render path (e.g. createEmptyJourney's race-condition guard). Do not use
  // this for reads from inside a render. getJourney reads `journeys` directly
  // below so consumers always see a fresh value on the render that follows a
  // state update.
  const journeysRef = useRef(journeys);
  // Track the current user id synchronously for write-through decisions in
  // callbacks (which close over a possibly-stale `user`).
  const userIdRef = useRef<string | null>(user?.id ?? null);
  useEffect(() => {
    userIdRef.current = user?.id ?? null;
  }, [user?.id]);

  useEffect(() => {
    journeysRef.current = journeys;
    // Persist to localStorage ONLY when anonymous. A signed-in user's trips
    // live in Supabase; writing them to localStorage too would leak one
    // account's trips to the next person on a shared device.
    if (!userIdRef.current) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(journeys));
      } catch {
        // localStorage may be full or disabled; silently ignore.
      }
    }
  }, [journeys]);

  // Load from Supabase when a user SIGNS IN. Critically, this must NOT
  // overwrite in-memory state in the anonymous case: `journeys` is already
  // initialized from localStorage in useState, and an active trip build
  // mutates that in-memory state. Re-running setJourneys(readLocalJourneys())
  // whenever authLoading resolves would clobber an in-progress build with a
  // stale snapshot (the bug that dropped freshly-added items). So we only act
  // on a real sign-in transition, keyed by user id, once per user.
  const loadedForUser = useRef<string | null>(null);
  useEffect(() => {
    // While auth is still resolving we don't yet know if a session exists,
    // so keep loading true and do nothing (consumers wait).
    if (authLoading) {
      setJourneysLoading(true);
      return;
    }

    // Signed out. If we had previously loaded a user's trips, this is a real
    // sign-out transition: drop their trips from memory and show the
    // anonymous (localStorage) view. If we never loaded a user (fresh
    // anonymous session), leave the in-memory state untouched so an active
    // build is never clobbered. Either way, anonymous load is synchronous.
    if (!user) {
      if (loadedForUser.current !== null) {
        loadedForUser.current = null;
        setJourneys(readLocalJourneys());
      }
      setJourneysLoading(false);
      return;
    }

    // Already loaded this user's trips? Don't reload (would clobber edits).
    if (loadedForUser.current === user.id) {
      setJourneysLoading(false);
      return;
    }
    loadedForUser.current = user.id;
    setJourneysLoading(true);

    const uid = user.id;
    let cancelled = false;
    (async () => {
      // Claim-on-sign-in: upsert any anonymous localStorage trips into the
      // account, then merge with whatever is already on the server.
      //
      // CRITICAL: only clear localStorage if EVERY claim upsert actually
      // succeeded. An earlier version swallowed upsert errors and cleared
      // localStorage unconditionally, so a single failed write destroyed the
      // local copy with nothing saved server-side -> total data loss on
      // refresh. We keep the local copy on any failure so the trip survives.
      const localTrips = readLocalJourneys();
      let allClaimed = true;
      if (localTrips.length > 0) {
        const results = await Promise.allSettled(
          localTrips.map((t) => upsertTrip(t, uid))
        );
        allClaimed = results.every((r) => r.status === "fulfilled");
        if (allClaimed) {
          try {
            localStorage.removeItem(STORAGE_KEY);
          } catch {
            /* ignore */
          }
        } else {
          console.error(
            "Claim-on-sign-in: some trips failed to upload; keeping local copy.",
            results
              .filter((r) => r.status === "rejected")
              .map((r) => (r as PromiseRejectedResult).reason)
          );
        }
      }
      const serverTrips = await listTrips();
      if (!cancelled) {
        if (!allClaimed && serverTrips.length === 0) {
          // Claim failed and server empty: fall back to local so the user
          // never sees an empty app after a partial sync.
          setJourneys(localTrips);
        } else {
          // MERGE, do not overwrite. listTrips() is async and can resolve
          // AFTER the user has started building a trip in memory; blindly
          // setting serverTrips would wipe that in-progress build (the
          // signed-in items-dropped bug). Keep any in-memory trip that is
          // newer than, or absent from, the server snapshot.
          setJourneys((current) => {
            const byId = new Map<string, Trip>();
            for (const t of serverTrips) byId.set(t.id, t);
            for (const local of current) {
              const server = byId.get(local.id);
              const localTime = Date.parse(local.updatedAt) || 0;
              const serverTime = server ? Date.parse(server.updatedAt) || 0 : -1;
              // Local wins when it's newer (an active edit) or not yet on the
              // server (a brand-new trip whose upsert is still in flight).
              if (!server || localTime >= serverTime) {
                byId.set(local.id, local);
              }
            }
            return Array.from(byId.values()).sort(
              (a, b) =>
                (Date.parse(b.updatedAt) || 0) - (Date.parse(a.updatedAt) || 0)
            );
          });
        }
        setJourneysLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, authLoading]);

  // Reads `journeys` directly so when state updates, this function is a new
  // reference, context value changes, and consumers re-render with the new
  // result. (Reading from journeysRef caused the new-trip Loading bug.)
  const getJourney = useCallback(
    (id: string) => journeys.find((j) => j.id === id),
    [journeys]
  );

  // Debounced write-through: when signed in, persist a trip to Supabase in
  // the background. Anonymous writes are handled by the localStorage effect.
  // Local state already updated optimistically so the UI never waits on the
  // network. The write is DEBOUNCED per trip id because during an AI
  // streaming response updateJourney fires many times a second (one per text
  // delta / tool call); without debouncing that would be dozens of upserts
  // per build. We coalesce to one write ~1.2s after the last change, keyed by
  // trip id, always sending the latest trip snapshot.
  const pendingWrites = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map()
  );
  const latestTrip = useRef<Map<string, Trip>>(new Map());

  const persist = useCallback((trip: Trip) => {
    const uid = userIdRef.current;
    if (!uid) return;
    latestTrip.current.set(trip.id, trip);
    const existing = pendingWrites.current.get(trip.id);
    if (existing) clearTimeout(existing);
    const handle = setTimeout(() => {
      pendingWrites.current.delete(trip.id);
      const snapshot = latestTrip.current.get(trip.id);
      latestTrip.current.delete(trip.id);
      if (snapshot && userIdRef.current) {
        void upsertTrip(snapshot, userIdRef.current).catch(() => {});
      }
    }, 1200);
    pendingWrites.current.set(trip.id, handle);
  }, []);

  // Flush any pending debounced writes on unmount so a fast navigate-away
  // right after an edit doesn't drop the last change.
  useEffect(() => {
    const writes = pendingWrites.current;
    const latest = latestTrip.current;
    return () => {
      writes.forEach((handle) => clearTimeout(handle));
      const uid = userIdRef.current;
      if (uid) {
        latest.forEach((snapshot) => {
          void upsertTrip(snapshot, uid).catch(() => {});
        });
      }
    };
  }, []);

  const saveJourney = useCallback(
    (trip: Trip) => {
      const stamped = { ...trip, updatedAt: new Date().toISOString() };
      setJourneys((prev) => {
        const idx = prev.findIndex((t) => t.id === trip.id);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = stamped;
          return next;
        }
        return [stamped, ...prev];
      });
      persist(stamped);
    },
    [persist]
  );

  const updateJourney = useCallback(
    (id: string, updater: (prev: Trip) => Trip) => {
      // The reducer MUST stay pure: this is the hot path during an AI build
      // (every add_day/add_item runs through here many times a second). An
      // earlier version called persist() INSIDE the updater; because persist
      // schedules timers and mutates refs, the impure reducer made React drop
      // intermediate updates under StrictMode/batching, so a day added by one
      // call wasn't visible to the next call's add_item (it logged "no day
      // found" and silently dropped the item). We capture the result and
      // persist it AFTER the state update instead.
      let updatedTrip: Trip | null = null;
      setJourneys((prev) => {
        const idx = prev.findIndex((t) => t.id === id);
        if (idx === -1) return prev;
        const next = [...prev];
        const updated = {
          ...updater(prev[idx]),
          updatedAt: new Date().toISOString(),
        };
        next[idx] = updated;
        updatedTrip = updated;
        return next;
      });
      if (updatedTrip) persist(updatedTrip);
    },
    [persist]
  );

  // Cancel any queued debounced upsert for a trip id so a pending write can't
  // resurrect a trip we're about to delete.
  const cancelPendingWrite = useCallback((id: string) => {
    const handle = pendingWrites.current.get(id);
    if (handle) clearTimeout(handle);
    pendingWrites.current.delete(id);
    latestTrip.current.delete(id);
  }, []);

  const deleteJourney = useCallback(
    (id: string) => {
      cancelPendingWrite(id);
      setJourneys((prev) => prev.filter((j) => j.id !== id));
      if (userIdRef.current) void deleteServerTrip(id).catch(() => {});
    },
    [cancelPendingWrite]
  );

  const clearAllJourneys = useCallback(() => {
    pendingWrites.current.forEach((handle) => clearTimeout(handle));
    pendingWrites.current.clear();
    latestTrip.current.clear();
    setJourneys([]);
    if (userIdRef.current) void deleteAllServerTrips().catch(() => {});
  }, []);

  const createEmptyJourney = useCallback(
    (id?: string): Trip => {
      const newId = id ?? generateUuid();
      // Synchronous read via ref is fine here because this is called from
      // useEffects (after render), not during render.
      const existing = journeysRef.current.find((j) => j.id === newId);
      if (existing) return existing;
      const empty = buildEmptyTrip(newId);
      setJourneys((prev) => {
        // Guard inside the functional update against concurrent double-creates
        // (React strict mode in dev, two effects firing simultaneously).
        if (prev.some((j) => j.id === newId)) return prev;
        return [empty, ...prev];
      });
      persist(empty);
      return empty;
    },
    [persist]
  );

  return (
    <JourneyContext.Provider
      value={{
        journeys,
        journeysLoading,
        getJourney,
        saveJourney,
        updateJourney,
        deleteJourney,
        clearAllJourneys,
        createEmptyJourney,
      }}
    >
      {children}
    </JourneyContext.Provider>
  );
}

export function useJourneys() {
  const ctx = useContext(JourneyContext);
  if (!ctx) {
    throw new Error("useJourneys must be used within a JourneyProvider");
  }
  return ctx;
}
